'use client';

import React, { useCallback } from 'react';
import { safeReactChild, sanitizeHtml, isSafeReactChild } from '@/lib/utils';

/**
 * Hook that provides safe content rendering functions
 * Prevents React Error #130 (Objects are not valid as React child)
 * Prevents React Error #418 (HTML elements cannot be used as React children)
 */
export function useSafeContent() {
  /**
   * Safely renders any content as a React child
   * This is the primary function to use for all content rendering
   */
  const renderSafe = useCallback((content: any): string => {
    try {
      return safeReactChild(content);
    } catch (error) {
      console.error('Error in renderSafe:', error);
      return '';
    }
  }, []);

  /**
   * Safely renders HTML content
   * Use this when you need to render HTML content
   */
  const renderSafeHtml = useCallback((html: any): string => {
    try {
      return sanitizeHtml(html);
    } catch (error) {
      console.error('Error in renderSafeHtml:', error);
      return '';
    }
  }, []);

  /**
   * Validates if content is safe to render as React child
   * Returns true if content can be safely rendered
   */
  const validateSafe = useCallback((content: any): boolean => {
    try {
      return isSafeReactChild(content);
    } catch (error) {
      console.error('Error in validateSafe:', error);
      return false;
    }
  }, []);

  /**
   * Comprehensive content sanitizer that handles all edge cases
   * Use this for any content that might come from APIs, user input, or external sources
   */
  const sanitizeAnyContent = useCallback((content: any): string => {
    try {
      // First check if it's already safe
      if (validateSafe(content)) {
        return renderSafe(content);
      }

      // If it's a string, sanitize as HTML
      if (typeof content === 'string') {
        return renderSafeHtml(content);
      }

      // For objects, arrays, or anything else, convert safely
      return renderSafe(content);
    } catch (error) {
      console.error('Error in sanitizeAnyContent:', error);
      return '';
    }
  }, [renderSafe, renderSafeHtml, validateSafe]);

  return {
    renderSafe,
    renderSafeHtml,
    validateSafe,
    sanitizeAnyContent
  };
}

/**
 * Global content safety interceptor
 * This can be used to wrap any content rendering operations
 */
export function withContentSafety<T extends any[], R>(
  fn: (...args: T) => R,
  fallback: R = '' as unknown as R
) {
  return (...args: T): R => {
    try {
      const result = fn(...args);
      
      // If the result looks problematic, return fallback
      if (typeof result === 'object' && result !== null && !React.isValidElement(result)) {
        console.warn('Function returned object, applying fallback');
        return fallback;
      }
      
      return result;
    } catch (error) {
      console.error('Error in withContentSafety:', error);
      return fallback;
    }
  };
}

/**
 * Hook for safely rendering translation content
 * Specifically designed to prevent translation-related React errors
 */
export function useSafeTranslation() {
  const { renderSafe, sanitizeAnyContent } = useSafeContent();

  /**
   * Safe translation renderer with comprehensive error handling
   */
  const safeT = useCallback((translationResult: any): string => {
    try {
      // Handle null/undefined
      if (translationResult === null || translationResult === undefined) {
        return '';
      }

      // Handle strings
      if (typeof translationResult === 'string') {
        return translationResult;
      }

      // Handle objects (common issue with i18next)
      if (typeof translationResult === 'object') {
        console.warn('Translation returned object, attempting to extract string:', translationResult);
        
        // Try common properties that might contain the actual translation
        if ('defaultValue' in translationResult && typeof translationResult.defaultValue === 'string') {
          return translationResult.defaultValue;
        }
        if ('en' in translationResult && typeof translationResult.en === 'string') {
          return translationResult.en;
        }
        if ('ar' in translationResult && typeof translationResult.ar === 'string') {
          return translationResult.ar;
        }
        
        // If it looks like a translation key object, use the key itself
        const keys = Object.keys(translationResult);
        if (keys.length === 1 && keys[0].includes('.')) {
          return keys[0]; // Return the key as fallback
        }
        
        // Convert to string representation
        return sanitizeAnyContent(translationResult);
      }

      // Handle arrays
      if (Array.isArray(translationResult)) {
        return renderSafe(translationResult);
      }

      // For any other type, convert to string
      return renderSafe(translationResult);
    } catch (error) {
      console.error('Error in safeT:', error);
      return '';
    }
  }, [renderSafe, sanitizeAnyContent]);

  return { safeT };
}